import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Empty, Form, InputNumber, Modal, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/it";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "sipli-fleet-core/components";
import { LIMIT_WARNING_ICON } from "src/assets/svgs";
import {
  useMutationResetCustomerLimit,
  useMutationUpdateCustomerLimits,
  useQueryGetCustomerLimits,
} from "src/queries/customerLimits.queries";
import {
  normalizeCustomerLimitsResponse,
  type CustomerLimitSummary,
} from "src/services/customerLimitsService";
import { getApiErrorMessage } from "src/util/apiError";
import { normalizeTimestampToMs } from "src/utils/dateTime";

interface LimitsTabProps {
  clientId?: string | number;
}

type LimitsFormValues = Record<string, number | null | undefined>;

const LimitWarningIcon = LIMIT_WARNING_ICON as unknown as ComponentType<{
  className?: string;
}>;

const knownLabelKeys: Record<string, string> = {
  AUTOMATIONS: "limit_code_automations",
  NOTIFICATION_SETTINGS: "limit_code_notification_settings",
  USERS: "limit_code_users",
  RECIPIENTS: "limit_code_recipients",
  INFO_NOTIFICATIONS_PER_MONTH: "limit_code_info_notifications_per_month",
  DDD_DRIVER_PARALLEL_EXTRACTIONS: "limit_code_ddd_driver_parallel_extractions",
  DDD_VEHICLE_PARALLEL_EXTRACTIONS: "limit_code_ddd_vehicle_parallel_extractions",
};

const extractResponse = (data: unknown): unknown => {
  const result = data as { data?: { response?: unknown }; response?: unknown } | null | undefined;
  return result?.data?.response ?? result?.response ?? null;
};

const getLimitLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  code: string
) => {
  const fallback = code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase());
  const key = knownLabelKeys[code];
  return key ? t(key, { defaultValue: fallback }) : fallback;
};

const getUsageColorClass = (used: number, limit: number | null) => {
  if (!limit || limit <= 0) return "customer-limit-progress-green";
  const ratio = used / limit;
  if (ratio >= 1) return "customer-limit-progress-red";
  if (ratio >= 0.6) return "customer-limit-progress-orange";
  return "customer-limit-progress-green";
};

const formatLimitValue = (value: number | null, language: string) => formatNumber(value, language);

const formatResetDate = (value: string | number | null | undefined, language: string) => {
  if (value === null || value === undefined || value === "") return null;

  const date = dayjs(normalizeTimestampToMs(value));

  return date.isValid() ? date.locale(language).format("D MMMM") : String(value);
};

const LimitsCardsSkeleton = () => (
  <div className="customer-limits-grid" aria-busy="true">
    {Array.from({ length: 5 }, (_, index) => (
      <section className="customer-limit-card customer-limit-card-skeleton" key={index}>
        <Skeleton active title={{ width: "45%" }} paragraph={{ rows: 2 }} />
      </section>
    ))}
  </div>
);

const LimitsTab = ({ clientId }: LimitsTabProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.split("-")[0] || "en";
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm<LimitsFormValues>();
  const [modal, contextHolder] = Modal.useModal();
  const [isEditing, setIsEditing] = useState(false);

  const customerLimitsQuery = useQueryGetCustomerLimits(clientId);
  const updateLimitsMutation = useMutationUpdateCustomerLimits(clientId);
  const resetLimitMutation = useMutationResetCustomerLimit(clientId);

  const limits = useMemo(
    () => normalizeCustomerLimitsResponse(extractResponse(customerLimitsQuery.data)),
    [customerLimitsQuery.data]
  );
  const configuredValues = useMemo(
    () =>
      limits.reduce((values, limit) => {
        values[limit.code] = limit.customerMaxLimit;
        return values;
      }, {} as LimitsFormValues),
    [limits]
  );

  useEffect(() => {
    setIsEditing(false);
    form.resetFields();
  }, [clientId, form]);

  useEffect(() => {
    if (!customerLimitsQuery.isError) return;

    messageApi.error(getApiErrorMessage(customerLimitsQuery.error) ?? t("error"));
  }, [customerLimitsQuery.error, customerLimitsQuery.isError, messageApi, t]);

  const handleReset = (code: string) => {
    modal.confirm({
      centered: true,
      className: "customer-limit-reset-confirm",
      icon: <LimitWarningIcon className="customer-limit-reset-confirm-icon" />,
      maskClosable: true,
      title: t("customer_limit_reset_notifications_title"),
      content: t("customer_limit_reset_notifications_message"),
      okText: t("confirm", { defaultValue: "Confirm" }),
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          await resetLimitMutation.mutateAsync(code);
          messageApi.success(t("customer_limit_notifications_reset_success"));
        } catch (error) {
          messageApi.error(t("error"));
          throw error;
        }
      },
    });
  };

  const handleSave = async (values: LimitsFormValues) => {
    const payload = limits.flatMap((limit) => {
      const value = values[limit.code];
      return typeof value === "number" && value !== limit.customerMaxLimit
        ? [{ code: limit.code, customerMaxLimit: value }]
        : [];
    });

    if (payload.length > 0) {
      try {
        await updateLimitsMutation.mutateAsync(payload);
        messageApi.success(t("customer_limits_saved_successfully"));
      } catch {
        messageApi.error(t("error"));
        return;
      }
    }
    setIsEditing(false);
  };
  // test
  const renderEditCard = (limit: CustomerLimitSummary) => {
    const maximum = limit.maxLimit ?? undefined;
    const isMonthlyNotification = limit.code === "INFO_NOTIFICATIONS_PER_MONTH";

    return (
      <section className="customer-limit-card" key={limit.code}>
        <h3>{getLimitLabel(t, limit.code)}</h3>
        <div className="customer-limit-divider" />
        <Form.Item
          name={limit.code}
          label={isMonthlyNotification ? t("creation_limit_per_month") : t("creation_limit")}
          validateTrigger="onChange"
          rules={[
            {
              type: "integer",
              min: 0,
              max: maximum,
              message: t("customer_limit_max_validation", { max: maximum }),
            },
          ]}
        >
          <InputNumber
            min={0}
            precision={0}
            placeholder={
              limit.maxLimit === null
                ? undefined
                : t("inherited_limit_placeholder", { value: limit.maxLimit })
            }
          />
        </Form.Item>
      </section>
    );
  };

  if (!clientId) {
    return (
      <div className="customer-limits-empty">
        <Empty description={t("no_data")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <section className="customer-limits-panel">
        <header className="customer-limits-header">
          <div>
            <h2>{t("limits")}</h2>
            <p>{t("customer_limits_description")}</p>
          </div>
          {isEditing ? (
            <div className="customer-limits-actions">
              <CustomButton
                label={t("cancel")}
                type="secondary"
                buttonSize="small"
                disabled={updateLimitsMutation.isPending}
                onClick={() => {
                  setIsEditing(false);
                }}
              />
              <CustomButton
                label={t("save")}
                buttonSize="small"
                loading={updateLimitsMutation.isPending}
                onClick={() => form.submit()}
              />
            </div>
          ) : !customerLimitsQuery.isError ? (
            <CustomButton
              disabled={customerLimitsQuery.isLoading || limits.length === 0}
              label={t("edit")}
              buttonSize="small"
              onClick={() => {
                form.setFieldsValue(configuredValues);
                setIsEditing(true);
              }}
            />
          ) : null}
        </header>

        {customerLimitsQuery.isLoading ? (
          <LimitsCardsSkeleton />
        ) : customerLimitsQuery.isError ? (
          <div className="customer-limits-empty">
            <Empty description={t("no_data")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : isEditing ? (
          <Form<LimitsFormValues>
            form={form}
            layout="vertical"
            className="customer-limits-grid"
            onFinish={handleSave}
          >
            {limits.map(renderEditCard)}
          </Form>
        ) : (
          <div className="customer-limits-grid">
            {!customerLimitsQuery.isLoading && limits.length === 0 ? t("no_data") : null}
            {limits.map((limit) => {
              const displayLimit = limit.customerMaxLimit ?? limit.maxLimit;
              const remaining =
                limit.remaining ??
                (displayLimit === null ? null : Math.max(displayLimit - limit.value, 0));
              const progress =
                displayLimit !== null && displayLimit > 0
                  ? Math.min((limit.value / displayLimit) * 100, 100)
                  : 0;
              const isNotificationCounter = limit.code === "INFO_NOTIFICATIONS_PER_MONTH";
              const resetDate = formatResetDate(limit.resetAt, language);

              return (
                <section className="customer-limit-card" key={limit.code}>
                  <div className="customer-limit-card-content">
                    <div className="customer-limit-card-header">
                      <div className="customer-limit-notification-tools">
                        <h3>{getLimitLabel(t, limit.code)}</h3>
                        {isNotificationCounter ? (
                          <Tooltip title={t("customer_limit_notifications_tooltip")}>
                            <span className="customer-limit-info">i</span>
                          </Tooltip>
                        ) : null}
                      </div>
                      {isNotificationCounter ? (
                        <CustomButton
                          label={t("reset")}
                          type="secondary"
                          buttonSize="small"
                          disabled={resetLimitMutation.isPending || limit.value === 0}
                          onClick={() => handleReset(limit.code)}
                        />
                      ) : null}
                    </div>
                    <div className="customer-limit-divider" />
                    {isNotificationCounter && resetDate ? (
                      <p className="customer-limit-reset-date">
                        {t("customer_limit_reset_date", { date: resetDate })}
                      </p>
                    ) : null}
                  </div>
                  <div className="customer-limit-content">
                    <div className="customer-limit-values">
                      <strong>
                        {formatNumber(limit.value, i18n.language)}{" "}
                        <span className="customer-limit-max-value">
                          / {formatLimitValue(displayLimit, i18n.language)}
                        </span>
                      </strong>
                      <span>{t("remaining_value", { value: formatLimitValue(remaining, i18n.language) })}</span>
                    </div>
                    <div className="customer-limit-progress-track">
                      <span
                        className={getUsageColorClass(limit.value, displayLimit)}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

export default LimitsTab;
